import React, { useEffect, useRef, useState } from 'react';
import { FileText, UploadCloud, X } from 'lucide-react';
import { t } from '../../i18n/t';
import './LopFilePicker.css';

export interface LopFilePickerProps {
  /** id of the (visually hidden but real) <input type="file"> - E2E uses setInputFiles('#id'). */
  id: string;
  files: File[];
  onChange: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  /** Folder picker (webkitdirectory). Implies multiple. */
  directory?: boolean;
  disabled?: boolean;
  /** Headline when nothing is chosen. */
  title?: string;
  /** Small line under the headline (accepted formats...). */
  hint?: string;
  /** Small line under the chosen file name. */
  replaceHint?: string;
  clearLabel?: string;
  className?: string;
}

/**
 * Styled drop zone / button fronting a hidden native file input. Click or drop;
 * shows the chosen file name(s); has a clear (x) button. Look matches the SwapUf import drop zone.
 */
export function LopFilePicker({
  id,
  files,
  onChange,
  accept,
  multiple,
  directory,
  disabled,
  title,
  hint,
  replaceHint,
  clearLabel,
  className,
}: LopFilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const many = !!multiple || !!directory;

  // Parent cleared the selection -> reset the native input too (so the same file can be picked again).
  useEffect(() => {
    if (files.length === 0 && inputRef.current) inputRef.current.value = '';
  }, [files]);

  // webkitdirectory is not in React's typings.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (directory) el.setAttribute('webkitdirectory', '');
    else el.removeAttribute('webkitdirectory');
  }, [directory]);

  const has = files.length > 0;
  const name = files.length === 1 ? files[0].name : t('common.filePicker.filesCount', { count: files.length });
  const defaultTitle = directory
    ? t('common.filePicker.chooseFolder')
    : many
      ? t('common.filePicker.chooseMany')
      : t('common.filePicker.choose');

  return (
    <div className={`lop-filepicker${className ? ` ${className}` : ''}`}>
      <label
        htmlFor={id}
        className={`lop-filepicker__zone${dragOver ? ' lop-filepicker__zone--over' : ''}${has ? ' lop-filepicker__zone--has-file' : ''}${disabled ? ' lop-filepicker__zone--disabled' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          const dropped = Array.from(e.dataTransfer.files ?? []);
          if (dropped.length > 0) onChange(many ? dropped : dropped.slice(0, 1));
        }}
      >
        {has ? <FileText size={22} /> : <UploadCloud size={22} />}
        <span className="lop-filepicker__text">
          {has ? (
            <>
              <strong>{name}</strong>
              <span className="lop-filepicker__hint">{replaceHint ?? t('common.filePicker.replace')}</span>
            </>
          ) : (
            <>
              <strong>{title ?? defaultTitle}</strong>
              {hint && <span className="lop-filepicker__hint">{hint}</span>}
            </>
          )}
        </span>
      </label>
      <input
        id={id}
        ref={inputRef}
        type="file"
        className="lop-filepicker__input"
        accept={accept}
        multiple={many}
        disabled={disabled}
        onChange={(e) => onChange(Array.from(e.target.files ?? []))}
      />
      {has && !disabled && (
        <button
          type="button"
          className="lop-filepicker__clear"
          aria-label={clearLabel ?? t('common.filePicker.clear')}
          title={clearLabel ?? t('common.filePicker.clear')}
          onClick={() => onChange([])}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
